import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const GOAL_LABELS = {
  ib_dp: "IB Board Exam (DP)",
  cambridge_igcse: "Cambridge (IGCSE / A-Levels)",
  cbse: "CBSE Board Exam",
  sat_math: "SAT Math Exam",
  jee_main_adv: "JEE Main / Advanced",
  general: "General Practice"
};

export default function TutorAssignments({ apiBase, token }) {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    async function loadAssignments() {
      try {
        const res = await fetch(`${apiBase}/api/tutor/assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load assignments");
        }
        const data = await res.json();
        setAssignments(data.assignments || []);
      } catch (err) {
        setError(err.message || "Failed to load assignments");
      } finally {
        setLoading(false);
      }
    }

    loadAssignments();
  }, [apiBase, token]);

  return (
    <div className="tutor-assignments-page" style={{ padding: "28px 24px", maxWidth: "1240px", margin: "0 auto" }}>
      {/* ── Top Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ margin: "0 0 6px 0", fontSize: "24px", fontWeight: "700", color: "#0f172a" }}>
            Assignments & Homework
          </h1>
          <p style={{ margin: 0, fontSize: "14px", color: "var(--muted)" }}>
            Review homework dispatched to students, submission status, and AI step diagnostics.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/my-questions")}
            style={{ padding: "10px 22px", display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <span>📚</span>
            <span>Assign from Question Bank</span>
          </button>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={{ backgroundColor: "#fee2e2", color: "#991b1b", padding: "12px 18px", borderRadius: "8px", marginBottom: "20px" }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="analysis-box">
          <div className="question-meta">Loading assignments...</div>
        </div>
      ) : assignments.length === 0 ? (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px dashed var(--border)",
            borderRadius: "14px",
            padding: "48px 24px",
            textAlign: "center"
          }}
        >
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📋</div>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: "600", color: "#0f172a" }}>
            No assignments dispatched yet
          </h3>
          <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "var(--muted)", maxWidth: "420px", marginLeft: "auto", marginRight: "auto" }}>
            Create assignments by selecting problems from your Question Bank and dispatching them to your enrolled students.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
            <button
              type="button"
              className="primary-button"
              onClick={() => navigate("/my-questions")}
              style={{ padding: "10px 22px", display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <span>📚</span>
              <span>Assign from Question Bank</span>
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {assignments.map((assignment) => {
            const isCompletedAll =
              assignment.assignedStudentCount > 0 &&
              assignment.submittedStudentCount === assignment.assignedStudentCount;
            const isTest = assignment.assignmentType === "test";
            const targetDue = assignment.dueTimestamp || assignment.dueDate;
            const isPastDue = Boolean(targetDue && new Date(targetDue) < new Date());

            return (
              <div
                key={assignment.id}
                className="tutor-assignment-card"
              >
                <div className="tutor-assignment-card-left">
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                    {/* Mode Tag: Timed Exam vs Homework */}
                    <span
                      style={{
                        backgroundColor: isTest ? "#fee2e2" : "#eff6ff",
                        color: isTest ? "#dc2626" : "#2563eb",
                        border: `1px solid ${isTest ? "#fca5a5" : "#bfdbfe"}`,
                        fontSize: "11px",
                        fontWeight: "700",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      <span>{isTest ? "⏱️" : "📝"}</span>
                      <span>{isTest ? "Timed Test / Exam" : "Homework Assignment"}</span>
                    </span>

                    {/* Grading Status Tag */}
                    {isTest && assignment.gradingStatus === "published" && (
                      <span
                        style={{
                          backgroundColor: "#ecfdf5",
                          color: "#059669",
                          border: "1px solid #a7f3d0",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <span>✓</span>
                        <span>Grades Published</span>
                      </span>
                    )}
                    {isTest && assignment.gradingStatus === "ai_graded" && (
                      <span
                        style={{
                          backgroundColor: "#ede9fe",
                          color: "#6d28d9",
                          border: "1px solid #ddd6fe",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        AI Graded (Review)
                      </span>
                    )}
                    {isTest && assignment.gradingStatus === "rubric_ready" && (
                      <span
                        style={{
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "2px 8px",
                          borderRadius: "6px"
                        }}
                      >
                        Rubric Ready
                      </span>
                    )}

                    {/* Homework Review Status Tag */}
                    {!isTest && (assignment.gradingStatus === "reviewed" || assignment.isReviewed) && (
                      <span
                        style={{
                          backgroundColor: "#ecfdf5",
                          color: "#059669",
                          border: "1px solid #a7f3d0",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <span>✓</span>
                        <span>Feedback Shared</span>
                      </span>
                    )}
                    {!isTest && !(assignment.gradingStatus === "reviewed" || assignment.isReviewed) && (assignment.submittedStudentCount || 0) > 0 && (
                      <span
                        style={{
                          backgroundColor: "#fffbeb",
                          color: "#d97706",
                          border: "1px solid #fde68a",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        Needs Review
                      </span>
                    )}

                    {/* Classroom Tag */}
                    {assignment.classroomName && (
                      <span
                        style={{
                          backgroundColor: "#f0fdf4",
                          color: "#166534",
                          border: "1px solid #bbf7d0",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "2px 8px",
                          borderRadius: "6px"
                        }}
                      >
                        🏫 {assignment.classroomName}
                      </span>
                    )}

                    {/* Target Curricular Goal Tag */}
                    {isTest && assignment.targetGoal && assignment.targetGoal !== "general" && (
                      <span
                        style={{
                          backgroundColor: "#ede9fe",
                          color: "#6d28d9",
                          border: "1px solid #ddd6fe",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "2px 8px",
                          borderRadius: "6px"
                        }}
                      >
                        🎯 {GOAL_LABELS[assignment.targetGoal] || assignment.targetGoal}
                      </span>
                    )}

                    {/* Total Marks Tag */}
                    {isTest && assignment.totalMarks > 0 && (
                      <span
                        style={{
                          backgroundColor: "#fef3c7",
                          color: "#92400e",
                          border: "1px solid #fde68a",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px"
                        }}
                      >
                        💯 {assignment.totalMarks} Marks
                      </span>
                    )}

                    {/* Grade Chip */}
                    <span
                      style={{
                        backgroundColor: "#f8fafc",
                        color: "#475569",
                        border: "1px solid #e2e8f0",
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "2px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      {assignment.grade || "Grade 11"}
                    </span>

                    {/* Chapter / Topic Chip */}
                    <span
                      style={{
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        fontSize: "11px",
                        fontWeight: "500",
                        padding: "2px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      {assignment.chapters?.[0] || "Mathematics"}
                    </span>

                    {/* Duration Chip for Tests */}
                    {isTest && (
                      <span
                        style={{
                          backgroundColor: "#fffbeb",
                          color: "#92400e",
                          border: "1px solid #fde68a",
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "6px"
                        }}
                      >
                        ⏱️ {assignment.durationMinutes || 60}m duration
                      </span>
                    )}

                    {/* Start Time for Tests */}
                    {isTest && assignment.startTime && (
                      <span style={{ fontSize: "12px", color: "#475569" }}>
                        Starts:{" "}
                        {new Date(assignment.startTime).toLocaleString("en-IN", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </span>
                    )}

                    {/* Due Date / Timestamp */}
                    {targetDue && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: isPastDue ? "#dc2626" : "var(--muted)",
                          fontWeight: isPastDue ? "600" : "normal"
                        }}
                      >
                        {isPastDue ? "Deadline Passed" : (isTest ? "Deadline" : "Due")}:{" "}
                        {isTest
                          ? new Date(targetDue).toLocaleString("en-IN", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : new Date(targetDue).toLocaleDateString("en-IN", {
                              month: "short",
                              day: "numeric"
                            })}
                      </span>
                    )}
                  </div>

                  <h3 style={{ margin: "0 0 6px 0", fontSize: "17px", fontWeight: "600", color: "#0f172a" }}>
                    {assignment.title}
                  </h3>
                  <div style={{ fontSize: "13px", color: "var(--muted)" }}>
                    {assignment.questionCount} {assignment.questionCount === 1 ? "problem" : "problems"} • Assigned to{" "}
                    {assignment.assignedStudentCount} {assignment.assignedStudentCount === 1 ? "student" : "students"}
                  </div>
                </div>

                {/* Submissions & Method Pills */}
                <div className="tutor-assignment-card-right">
                  <div style={{ textAlign: "right", minWidth: "120px" }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: isCompletedAll ? "#059669" : "#0f172a"
                      }}
                    >
                      {assignment.submittedStudentCount} / {assignment.assignedStudentCount} submitted
                    </div>
                    {isTest ? (
                      <div
                        style={{
                          fontSize: "12px",
                          color: assignment.gradingStatus === "published" ? "#059669" : "var(--muted)",
                          fontWeight: assignment.gradingStatus === "published" ? "600" : "normal",
                          marginTop: "2px"
                        }}
                      >
                        {assignment.gradingStatus === "published" ? (
                          <span>✓ Published {assignment.averageScore !== null ? `(Avg: ${assignment.averageScore}%)` : ""}</span>
                        ) : assignment.averageScore !== null ? (
                          `Cohort Avg: ${assignment.averageScore}%`
                        ) : (
                          "Awaiting grading"
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "12px",
                          color: (assignment.gradingStatus === "reviewed" || assignment.isReviewed)
                            ? "#059669"
                            : (assignment.submittedStudentCount || 0) > 0
                            ? "#d97706"
                            : isCompletedAll
                            ? "#059669"
                            : "var(--muted)",
                          fontWeight: (assignment.gradingStatus === "reviewed" || assignment.isReviewed) ? "600" : "500",
                          marginTop: "2px"
                        }}
                      >
                        {(assignment.gradingStatus === "reviewed" || assignment.isReviewed) ? (
                          <span>✓ Feedback Shared</span>
                        ) : (assignment.submittedStudentCount || 0) > 0 ? (
                          <span>Needs Review</span>
                        ) : isCompletedAll ? (
                          "✓ All Completed"
                        ) : (
                          "Practice / Qualitative"
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "6px" }}>
                    {assignment.slateSubmissions > 0 && (
                      <span
                        title={`${assignment.slateSubmissions} submissions via Digital Slate`}
                        style={{
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                          fontSize: "12px",
                          padding: "5px 10px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontWeight: "500"
                        }}
                      >
                        🖊️ {assignment.slateSubmissions}
                      </span>
                    )}
                    {assignment.photoSubmissions > 0 && (
                      <span
                        title={`${assignment.photoSubmissions} submissions via Notebook Photo`}
                        style={{
                          backgroundColor: "#fffbeb",
                          color: "#b45309",
                          border: "1px solid #fde68a",
                          fontSize: "12px",
                          padding: "5px 10px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontWeight: "500"
                        }}
                      >
                        📷 {assignment.photoSubmissions}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => navigate(`/tutor/assignments/${assignment.id}`)}
                    style={{ fontSize: "13px", padding: "8px 18px", whiteSpace: "nowrap" }}
                  >
                    View Submissions →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

